import { useState, useMemo } from "react";

/**
 * Custom hook for table sorting functionality
 *
 * @param {Array} data - The array of data to sort
 * @param {Object} config - Optional configuration
 * @param {string} config.initialKey - Initial sort column key
 * @param {string} config.initialDirection - Initial direction ('asc' or 'desc')
 * @param {Object} customComparators - Optional custom comparison functions for specific columns
 * @returns {Object} - Sorted data and sort control functions
 */
const useTableSort = (data, config = {}, customComparators = {}) => {
  const [sortConfig, setSortConfig] = useState({
    key: config.initialKey || null,
    direction: config.initialDirection || "asc",
  });

  // Sort the data based on current sort configuration
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !data?.length) return data;

    const sorted = [...data].sort((a, b) => {
      // Check if there's a custom comparator for this column
      if (customComparators[sortConfig.key]) {
        return customComparators[sortConfig.key](a, b, sortConfig.direction);
      }

      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle different data types
      // Numbers
      if (typeof aVal === "number" && typeof bVal === "number") {
        return aVal - bVal;
      }

      // Dates (ISO strings or Date objects)
      const aDate = new Date(aVal);
      const bDate = new Date(bVal);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return aDate.getTime() - bDate.getTime();
      }

      // Strings (case-insensitive)
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return aStr.localeCompare(bStr);
    });

    return sortConfig.direction === "desc" ? sorted.reverse() : sorted;
  }, [data, sortConfig, customComparators]);

  /**
   * Request sort by a specific column
   * Toggles direction if clicking the same column
   */
  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  /**
   * Get CSS class names for a sortable header cell
   */
  const getSortClass = (key) => {
    if (sortConfig.key !== key) return "sortable";
    return `sortable sort-active sort-${sortConfig.direction}`;
  };

  /**
   * Reset sort to initial state
   */
  const resetSort = () => {
    setSortConfig({
      key: config.initialKey || null,
      direction: config.initialDirection || "asc",
    });
  };

  return {
    sortedData,
    sortConfig,
    requestSort,
    getSortClass,
    resetSort,
  };
};

export default useTableSort;
